// pothos manager «Отгрузки» — index browser over pothos_assets (ADR-0034).
// Req/Res for: assets query (grid), facets, merchant coverage (по стране),
// uploader typeahead. Image bytes go via GET /api/assetImage?id= (public,
// unguessable uuid — same model as /api/photo).
import { z } from 'zod';
import { merchantNameOk } from './merchant';

// «Принятое | Отклонённое | Все» + tight per-status filtering when needed.
export const PothosAssetsFilterSchema = z.object({
  category: z.string().trim().max(40).default(''),   // event_ticket|coupon|gift_card|membership|''
  modality: z.string().trim().max(24).default(''),   // photo|screenshot|photo_screenshot|''
  country: z.string().trim().max(40).default(''),
  locale: z.string().trim().max(40).default(''),
  merchant: z.string().trim().max(80).default(''),   // exact (lowercased canon)
  uploader: z.string().trim().max(160).default(''),  // uploader_account exact
  asset_role: z.string().trim().max(10).default(''), // final|source|''
  verdict: z.enum(['', 'accepted', 'rejected']).default(''),
  statuses: z.array(z.string().trim().max(24)).max(10).default([]),
  shipped_batch: z.string().trim().max(40).default(''),
});
export type PothosAssetsFilter = z.infer<typeof PothosAssetsFilterSchema>;

export const ASSETS_PAGE_MAX = 120;

export const PothosManagerAssetsReqSchema = PothosAssetsFilterSchema.extend({
  offset: z.number().int().nonnegative().max(1_000_000).default(0),
  limit: z.number().int().positive().max(ASSETS_PAGE_MAX).default(60),
});
export type PothosManagerAssetsReq = z.infer<typeof PothosManagerAssetsReqSchema>;

export const PothosManagerAssetRowSchema = z.object({
  asset_id: z.string(),
  category: z.string(),
  modality: z.string(),
  merchant: z.string(),
  country: z.string(),
  locale: z.string(),
  status: z.string(),
  asset_role: z.string(),
  uploaded_via: z.string(),
  uploader_account: z.string(),
  uploaded_at: z.string(),
  shipped_batch: z.string(),
  file_type: z.string(),
  size_bytes: z.number(),
  reject_reason: z.string(),
  client_verdict: z.string(),
  client_comment: z.string().default(''),
  has_bytes: z.boolean(),        // false → метаданные без картинки (R2 ещё не долит)
  thumb_url: z.string(),         // '' when !has_bytes
  full_url: z.string(),
  // Поля отгрузочной таблицы (Metadata_Participant_File): их проставляет
  // finalizePackaging после ручного приёма. Пустые = кадр ещё не упакован
  // (крон не дошёл) либо стоит на pack_hold — в отгрузку такой не берём.
  participant_id: z.string().default(''),
  pass_type: z.string().default(''),
  bar_code_type: z.string().default(''),
  ship_file_name: z.string().default(''),   // IMG_####
  image_grouping: z.number().default(0),
  pack_uid: z.string().default(''),         // PID_PassType_Grouping
  pack_image_id: z.string().default(''),    // PID_FileName_PassType_Merchant_Grouping
  device: z.string().default(''),
  secondary_locale: z.string().default(''),  // 14-я колонка трекера
});
export type PothosManagerAssetRow = z.infer<typeof PothosManagerAssetRowSchema>;

// Строка, готовая к отгрузке. Жёсткий контракт (правило Глеба 2026-07-27):
// в архив и в Metadata_Participant_File не уходит НИ ОДНА строка с дырой —
// пустой мерчант, отсутствующий PID, Pass_Type, Bar Code Type или файл
// нумерации делают кадр непригодным для заказчика. Такие кадры разбирает
// крон `merchant-gate` (vision → доп. проверка QA), а не выгрузка.
export const ShipmentReadyRowSchema = PothosManagerAssetRowSchema.extend({
  participant_id: z.string().min(1),
  merchant: z.string().min(1).refine(merchantNameOk, {
    message: 'merchant must be latin letters, digits and spaces only',
  }),
  pass_type: z.string().min(1),
  bar_code_type: z.string().min(1),
  ship_file_name: z.string().min(1),
  image_grouping: z.number().int().positive(),
  has_bytes: z.literal(true),
  // Кадр без страны/языка — дыра в Metadata_Participant_File и строка,
  // которую матрица «Текущей отгрузки» показать не может (страну не к чему
  // отнести). До 2026-08-31 такой кадр УХОДИЛ в архив и давал расхождение
  // счётчика с таблицей (662 против 661) — теперь оба диалекта его держат.
  country: z.string().min(1),
  locale: z.string().min(1),
});
export type ShipmentReadyRow = z.infer<typeof ShipmentReadyRowSchema>;

// ── ТО ЖЕ ПРАВИЛО НА ДИАЛЕКТЕ D1 ────────────────────────────────────────────
// Сборка zip судит строку зодом (выше) уже В БРАУЗЕРЕ, поэтому счётчик «готово
// к отгрузке» на сервере обязан считать ТЕМ ЖЕ правилом — иначе «Обзор»
// обещает 1683, а в архив уходит 984, и разница не видна никому до сборки.
//
// ⚠️ Меняешь `ShipmentReadyRowSchema` — меняй и это выражение В ТОМ ЖЕ коммите.
// Они намеренно лежат рядом: два диалекта одного контракта.
//
// `merchant NOT GLOB '*[^ A-Za-z0-9]*'` — SQL-приближение `merchantNameOk`:
// ловит диакритику, апострофы, иероглифы и знаки. Двойные пробелы и края
// зод считает ненормализованными, GLOB их пропустит — это заведомая, узкая
// щель, а не забытый случай (такие имена чинит `merchant-gate`).
export const SHIP_READY_SQL =
  "IFNULL(participant_id,'') <> '' AND IFNULL(merchant,'') <> '' " +
  "AND IFNULL(pass_type,'') <> '' AND IFNULL(bar_code_type,'') <> '' " +
  "AND IFNULL(ship_file_name,'') <> '' AND IFNULL(image_grouping,0) > 0 " +
  "AND bytes_present = 1 AND merchant NOT GLOB '*[^ A-Za-z0-9]*' " +
  "AND IFNULL(country,'') <> '' AND IFNULL(locale,'') <> ''";

// Почему строка НЕ уйдёт в архив — одна причина на строку, в порядке
// «сначала то, что решает человек». Ключи совпадают с `SHIP_BLOCK_LABELS`.
//
// `finalizing` — отдельная от `unpacked` причина: это ОЧЕРЕДЬ крона
// finalizePackaging (кадр принят QA и ждёт PID/IMG — уедет сам, вопрос
// времени), тогда как `unpacked` — застрявшие строки, которые крон не
// возьмёт (пустой dept_qa, чеки). Условие обязано зеркалить выборку
// finalizePackaging (participant_id='' ∧ dept_qa<>'' ∧ pack_hold=''
// ∧ category<>'receipt'); pack_hold и байты уже отсечены ветками выше.
export const SHIP_BLOCK_REASON_SQL =
  "CASE " +
  "WHEN IFNULL(pack_hold,'') <> '' THEN 'hold' " +
  "WHEN bytes_present <> 1 THEN 'no_bytes' " +
  "WHEN IFNULL(participant_id,'') = '' AND IFNULL(dept_qa,'') <> '' " +
  "  AND category <> 'receipt' THEN 'finalizing' " +
  "WHEN IFNULL(participant_id,'') = '' OR IFNULL(ship_file_name,'') = '' " +
  "  OR IFNULL(image_grouping,0) = 0 OR IFNULL(pass_type,'') = '' THEN 'unpacked' " +
  "WHEN IFNULL(bar_code_type,'') = '' THEN 'no_barcode' " +
  "WHEN IFNULL(merchant,'') = '' THEN 'no_merchant' " +
  "WHEN merchant GLOB '*[^ A-Za-z0-9]*' THEN 'merchant_bad' " +
  "WHEN IFNULL(country,'') = '' OR IFNULL(locale,'') = '' THEN 'no_locale' " +
  "ELSE 'ok' END";

export const SHIP_BLOCK_LABELS: Record<string, string> = {
  // pack_hold с 2026-08-31 держит не капы (кап-кадр уходит в rejected), а
  // ручные откладывания вроде `layout-redo` — подпись «на капах» врала.
  hold: 'отложены (pack_hold)',
  finalizing: 'ждут финализации',
  unpacked: 'застряли без упаковки',
  no_barcode: 'без Bar Code Type',
  no_merchant: 'без мерчанта',
  merchant_bad: 'мерчант не латиницей',
  no_bytes: 'нет файла в R2',
  no_locale: 'без страны/языка',
};

export const PothosShipReadinessSchema = z.object({
  accepted: z.number(),   // всего в статусе accepted — «потенциал»
  packable: z.number(),   // реально уйдёт в архив ПРЯМО СЕЙЧАС
  blocked: z.array(z.object({ reason: z.string(), n: z.number() })),
});
export type PothosShipReadiness = z.infer<typeof PothosShipReadinessSchema>;

export const PothosManagerAssetsResSchema = z.object({
  total: z.number(),
  offset: z.number(),
  rows: z.array(PothosManagerAssetRowSchema),
});
export type PothosManagerAssetsRes = z.infer<typeof PothosManagerAssetsResSchema>;

const FacetSchema = z.array(z.object({ value: z.string(), n: z.number() }));
export const PothosManagerAssetsFacetsReqSchema = PothosAssetsFilterSchema;
export const PothosManagerAssetsFacetsResSchema = z.object({
  category: FacetSchema,
  modality: FacetSchema,
  status: FacetSchema,
  country: FacetSchema,   // top-40
  merchant: FacetSchema,  // top-60 within the current filter
});
export type PothosManagerAssetsFacetsRes = z.infer<typeof PothosManagerAssetsFacetsResSchema>;

export const PothosManagerMerchantCoverageReqSchema = z.object({
  country: z.string().trim().min(1).max(40),
  category: z.string().trim().max(40).default(''),
  modality: z.string().trim().max(24).default(''),
});
export const PothosManagerMerchantCoverageResSchema = z.object({
  rows: z.array(z.object({
    merchant: z.string(),
    total: z.number(),
    shipped: z.number(),     // shipped + client_accepted
    accepted: z.number(),    // accepted (внутренне принято, не отгружено)
    rejected: z.number(),    // rejected + client_rejected + duplicate
    in_work: z.number(),     // uploaded + in_work + qa
  })),
});
export type PothosManagerMerchantCoverageRes = z.infer<typeof PothosManagerMerchantCoverageResSchema>;

// «Планирование»: остатки по подкатегориям — план (locale_limits) vs
// физически отгружено (каталог) vs доступные исходники для производства.
export const PothosManagerPlanningRowSchema = z.object({
  locale_code: z.string(),
  priority: z.string(),
  category: z.string(),
  modality: z.string(),
  plan: z.number(),
  shipped: z.number(),       // shipped + client_accepted + client_rejected
  remaining: z.number(),     // max(0, plan − shipped)
  sources: z.number(),       // asset_role='source', ещё не отработанные
  in_work: z.number(),       // uploaded/in_work/qa/accepted (не отгружено)
});
export const PothosManagerPlanningResSchema = z.object({
  rows: z.array(PothosManagerPlanningRowSchema),
});
export type PothosManagerPlanningRes = z.infer<typeof PothosManagerPlanningResSchema>;

export const PothosManagerUploaderSuggestReqSchema = z.object({
  q: z.string().trim().min(1).max(120),
});
export const PothosManagerUploaderSuggestResSchema = z.object({
  items: z.array(z.object({ account: z.string(), n: z.number() })),
});
export type PothosManagerUploaderSuggestRes = z.infer<typeof PothosManagerUploaderSuggestResSchema>;
